/**
 * CREATE YOUTUBE ADS — /google-ads/youtube/create/ad[?adgroup_id=…]
 *
 * One YouTube (Demand Gen) ad group, MANY ad drafts (add / duplicate /
 * remove), created in order — the same video ad the campaign builder makes:
 * a YouTube video plus business name, headline, long headline, description,
 * landing page and a square logo. Standard Video ad groups are read-only
 * through the Google Ads API, so only Demand Gen groups are offered.
 *
 * GOOGLE ADS DOES NOT HOST VIDEO: each ad references a video already on the
 * business's YouTube channel — the user pastes its URL (or 11-char id).
 *
 * Publishing is RESUMABLE: created ads are remembered by draft id, so a
 * mid-way rejection can be fixed and re-submitted without duplicates.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Input } from '@/components/Input';
import { Select } from '@/components/Select';
import { FormField } from '@/components/FormField';
import toast from 'react-hot-toast';
import { CheckCircle2, Circle, Copy, Film, Plus, Trash2, Upload, XCircle } from 'lucide-react';
import { googleAdsService, GoogleAdsAdGroupRow } from '@/services/google-ads.service';
import { LibraryFile, MEDIA_FOLDERS, uploadsService } from '@/services/uploads.service';
import { MediaLibraryModal } from '@/components/MediaLibraryModal';
import { parseYoutubeId } from '@/pages/google-ads/googleAdsTargeting';
import {
  BackTo, ErrorBox, errMsg, Label, Spinner, Tag, useYoutubeAuth, VideoThumb, YT_BASE,
} from '@/components/google-ads/youtube/youtubeShared';

const uid = () => Math.random().toString(36).slice(2, 10);

type AdDraft = {
  id: string; name: string; video: string; businessName: string; headline: string; longHeadline: string;
  description: string; finalUrl: string; logoUrl: string; logoNote: string | null;
};

const newAd = (n: number, from?: AdDraft): AdDraft => ({
  id: uid(), name: `Ad ${n}`, video: '', headline: '', longHeadline: '', description: '',
  // The business, landing page and logo rarely change between ads in one group.
  businessName: from?.businessName ?? '', finalUrl: from?.finalUrl ?? '', logoUrl: from?.logoUrl ?? '', logoNote: from?.logoNote ?? null,
});
const cloneAd = (a: AdDraft): AdDraft => ({ ...a, id: uid(), name: a.name ? `${a.name} (copy)` : '' });

const adIssues = (a: AdDraft): string[] => {
  const i: string[] = [];
  if (!a.name.trim()) i.push('ad name');
  if (!parseYoutubeId(a.video)) i.push('a valid YouTube video URL');
  if (!a.businessName.trim()) i.push('business name');
  if (!a.headline.trim()) i.push('headline');
  if (!a.longHeadline.trim()) i.push('long headline');
  if (!a.description.trim()) i.push('description');
  if (!/^https?:\/\//i.test(a.finalUrl.trim())) i.push('landing page URL (https://…)');
  if (!a.logoUrl) i.push('square logo');
  return i;
};

const logoIssue = (f: LibraryFile): string | null => {
  if (!f.width || !f.height) return null;
  if (f.width < 128 || f.height < 128) return `${f.width}×${f.height} — Google needs 128×128 minimum`;
  if (Math.abs(f.width / f.height - 1) > 0.01) return `must be square; this is ${f.width}×${f.height}`;
  return null;
};

type P = 'idle' | 'running' | 'done' | 'failed';
const StepIcon = ({ s }: { s?: P }) =>
  s === 'done' ? <CheckCircle2 size={15} className="shrink-0 text-success" />
  : s === 'running' ? <Spinner size={15} className="text-primary" />
  : s === 'failed' ? <XCircle size={15} className="shrink-0 text-danger" />
  : <Circle size={15} className="shrink-0 text-text-muted/40" />;

export default function CreateYouTubeAd() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { accountUid } = useYoutubeAuth();

  const [adgroups, setAdgroups] = useState<GoogleAdsAdGroupRow[]>([]);
  const [adgroupId, setAdgroupId] = useState(params.get('adgroup_id') || '');

  const first = useMemo(() => newAd(1), []);
  const [ads, setAds] = useState<AdDraft[]>([first]);
  const [openId, setOpenId] = useState<string>(first.id);
  const patch = (aid: string, p: Partial<AdDraft>) =>
    setAds((as) => as.map((a) => (a.id === aid ? { ...a, ...p } : a)));

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pubMap, setPubMap] = useState<Record<string, P>>({});
  const madeRef = useRef<Record<string, string>>({});
  /** Which ad's logo the library modal is picking for. */
  const [logoPick, setLogoPick] = useState<string | null>(null);

  useEffect(() => {
    if (!accountUid) return;
    Promise.all([googleAdsService.listAdGroups(accountUid), googleAdsService.listCampaigns(accountUid)])
      .then(([groups, campaigns]) => {
        const demandGen = new Set(campaigns.filter((c) => c.advertisingChannelType === 'DEMAND_GEN' && c.status !== 'REMOVED').map((c) => c.id));
        setAdgroups(groups.filter((g) => demandGen.has(g.campaignId) && g.status !== 'REMOVED'));
      })
      .catch(() => setAdgroups([]));
  }, [accountUid]);

  const uploadLogo = async (aid: string, file: File | null) => {
    if (!file) return;
    patch(aid, { logoNote: 'Uploading…' });
    try {
      const kit = await uploadsService.imagekit(file, MEDIA_FOLDERS.googleYoutube);
      if (!kit.url) throw new Error('Upload returned no URL.');
      if (kit.width && kit.height) {
        if (kit.width < 128 || kit.height < 128) throw new Error(`Logo is ${kit.width}×${kit.height}; Google needs 128×128 minimum.`);
        if (Math.abs(kit.width / kit.height - 1) > 0.01) throw new Error(`Logo must be square; this is ${kit.width}×${kit.height}.`);
      }
      patch(aid, { logoUrl: kit.url, logoNote: file.name });
    } catch (e) {
      patch(aid, { logoUrl: '', logoNote: errMsg(e) });
    }
  };

  const issues = useMemo(() => {
    const i: string[] = [];
    if (!adgroupId) i.push('Pick the ad group these ads belong to.');
    if (ads.length === 0) i.push('Add at least one ad.');
    ads.forEach((a) => {
      const miss = adIssues(a);
      if (miss.length) i.push(`${a.name || 'Untitled ad'}: needs ${miss.join(', ')}.`);
    });
    return i;
  }, [adgroupId, ads]);

  const submit = async () => {
    setError(null);
    if (!accountUid) return setError('Connect a Google Ads account first.');
    if (issues.length) return setError(issues[0]);

    setSaving(true);
    const mark = (k: string, v: P) => setPubMap((m) => ({ ...m, [k]: v }));
    try {
      for (const a of ads) {
        if (madeRef.current[a.id]) continue;
        mark(a.id, 'running');
        try {
          const res = await googleAdsService.youtube.createDemandGenAd(accountUid, {
            adGroupId: adgroupId, name: a.name.trim(), youtubeVideoId: parseYoutubeId(a.video) ?? '', logoUrl: a.logoUrl,
            finalUrl: a.finalUrl.trim(), businessName: a.businessName.trim(), headline: a.headline.trim(),
            longHeadline: a.longHeadline.trim(), description: a.description.trim(),
          });
          madeRef.current[a.id] = res.resourceName;
        } catch (e) {
          mark(a.id, 'failed'); setError(`${a.name}: ${errMsg(e)}`); return;
        }
        mark(a.id, 'done');
      }
      toast.success(`${ads.length === 1 ? 'Ad' : `${ads.length} ads`} created — Google reviews every ad; the campaign stays paused until you enable it.`);
      navigate(YT_BASE);
    } finally { setSaving(false); }
  };

  return (
    <>
      <Topbar
        title="New YouTube Ads"
        breadcrumb="Google Ads / YouTube"
        description="Videos from your YouTube channel plus the ad text — add as many ads as the group needs"
        back={<BackTo to={YT_BASE} label="Back to YouTube Ads" />}
      />
      <div className="w-full px-7 py-7">
        <div className="mx-auto w-full max-w-2xl space-y-4">
          <Card className="p-6">
            <FormField label="Ad group" required
              hintBelow hint="Google Ads references YouTube — it does not host video files. Upload each video to the business's YouTube channel first, then paste its link below.">
              <Select value={adgroupId} onChange={(e) => setAdgroupId(e.target.value)} disabled={Object.keys(madeRef.current).length > 0}>
                <option value="">Pick an ad group</option>
                {adgroups.map((g) => <option key={g.id} value={g.id}>{g.campaignName ? `${g.campaignName} › ` : ''}{g.name}</option>)}
              </Select>
            </FormField>
          </Card>

          {ads.map((a, n) => {
            const open = openId === a.id;
            const made = !!madeRef.current[a.id];
            const miss = adIssues(a);
            const vid = parseYoutubeId(a.video);
            return (
              <Card key={a.id} padded={false} className="overflow-hidden">
                <div className="flex items-center gap-2.5 border-b border-border px-4 py-3">
                  <button type="button" className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
                    onClick={() => setOpenId(open ? '' : a.id)}>
                    <StepIcon s={pubMap[a.id]} />
                    <VideoThumb videoId={vid} />
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold text-text-primary">{a.name || `Ad ${n + 1}`}</div>
                      <div className="truncate text-2xs text-text-muted">
                        {vid ? `youtu.be/${vid}` : 'No video yet'}
                        {a.headline ? ` · ${a.headline}` : ''}
                      </div>
                    </div>
                  </button>
                  {made ? <Tag tone="ok">created</Tag>
                    : miss.length === 0 ? <Tag tone="ok">ready</Tag>
                    : <Tag tone="warn">{miss.length} missing</Tag>}
                  <button type="button" className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-text-primary"
                    title="Duplicate ad" aria-label="Duplicate ad" disabled={saving}
                    onClick={() => {
                      const copy = cloneAd(a);
                      setAds((as) => { const i = as.findIndex((x) => x.id === a.id); return [...as.slice(0, i + 1), copy, ...as.slice(i + 1)]; });
                      setOpenId(copy.id);
                    }}>
                    <Copy size={14} />
                  </button>
                  {ads.length > 1 && !made && (
                    <button type="button" className="flex h-7 w-7 items-center justify-center rounded-lg text-text-muted hover:bg-surface-alt hover:text-danger"
                      title="Remove ad" aria-label="Remove ad" disabled={saving}
                      onClick={() => setAds((as) => as.filter((x) => x.id !== a.id))}>
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>

                {open && (
                  <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Label>YouTube video URL</Label>
                      <Input className="mt-1" aria-label="YouTube video URL" placeholder="https://www.youtube.com/watch?v=…" value={a.video} disabled={made}
                        onChange={(e) => patch(a.id, { video: e.target.value })} />
                    </div>
                    <div>
                      <Label>Ad name (internal)</Label>
                      <Input className="mt-1" aria-label="Ad name" placeholder="e.g. Promo video v1" value={a.name} disabled={made}
                        onChange={(e) => patch(a.id, { name: e.target.value })} />
                    </div>
                    <div>
                      <Label>Business name (25)</Label>
                      <Input className="mt-1" aria-label="Business name" maxLength={25} value={a.businessName} disabled={made}
                        onChange={(e) => patch(a.id, { businessName: e.target.value })} />
                    </div>
                    <div>
                      <Label>Headline (40)</Label>
                      <Input className="mt-1" aria-label="Headline" maxLength={40} value={a.headline} disabled={made}
                        onChange={(e) => patch(a.id, { headline: e.target.value })} />
                    </div>
                    <div>
                      <Label>Long headline (90)</Label>
                      <Input className="mt-1" aria-label="Long headline" maxLength={90} value={a.longHeadline} disabled={made}
                        onChange={(e) => patch(a.id, { longHeadline: e.target.value })} />
                    </div>
                    <div>
                      <Label>Description (90)</Label>
                      <Input className="mt-1" aria-label="Description" maxLength={90} value={a.description} disabled={made}
                        onChange={(e) => patch(a.id, { description: e.target.value })} />
                    </div>
                    <div>
                      <Label>Call to action</Label>
                      <div className="mt-1 rounded-lg border border-border bg-surface-alt px-3 py-2 text-sm text-text-secondary">Automatic — selected by Google</div>
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Landing page URL</Label>
                      <Input className="mt-1" aria-label="Landing page URL" placeholder="https://…" value={a.finalUrl} disabled={made}
                        onChange={(e) => patch(a.id, { finalUrl: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                      <Label>Logo — square, min 128×128 (required)</Label>
                      <div className="mt-1 flex gap-2">
                        <label className={`flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-dashed border-border px-3 py-2 text-xs text-text-muted ${made ? 'opacity-60' : 'cursor-pointer hover:bg-surface-alt'}`}>
                          {a.logoUrl ? <CheckCircle2 size={14} className="shrink-0 text-success" /> : <Upload size={14} className="shrink-0" />}
                          <span className="truncate">{a.logoNote || 'Upload a logo image…'}</span>
                          <input type="file" accept="image/png,image/jpeg,image/gif" className="hidden" disabled={made}
                            onChange={(e) => { uploadLogo(a.id, e.target.files?.[0] ?? null); e.currentTarget.value = ''; }} />
                        </label>
                        <button type="button" onClick={() => setLogoPick(a.id)} disabled={made}
                          className="shrink-0 rounded-lg border border-border bg-surface px-3 py-2 text-xs font-medium text-text-primary hover:border-primary/50 hover:bg-surface-alt disabled:opacity-60">
                          Library
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}

          <Button variant="secondary" className="w-full" iconLeft={<Plus size={15} />} disabled={saving}
            onClick={() => { const a = newAd(ads.length + 1, ads[ads.length - 1]); setAds((as) => [...as, a]); setOpenId(a.id); }}>
            Add another ad
          </Button>

          {error && <ErrorBox message={error} />}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="secondary" onClick={() => navigate(YT_BASE)} disabled={saving}>Cancel</Button>
            <Button onClick={submit} loading={saving} disabled={saving || issues.length > 0} iconLeft={<Film size={15} />}>
              {error ? 'Retry' : ads.length === 1 ? 'Create ad' : `Create ${ads.length} ads`}
            </Button>
          </div>
        </div>
      </div>
      <MediaLibraryModal open={!!logoPick} onClose={() => setLogoPick(null)} folder={MEDIA_FOLDERS.googleYoutube} type="image" multiple={false}
        title="Logos in your library" validate={logoIssue}
        onPick={(files) => { const f = files[0]; if (f && logoPick) patch(logoPick, { logoUrl: f.url, logoNote: f.name }); }} />
    </>
  );
}
